import { Box, Typography, useTheme } from '@mui/material'
import { GridColDef, GridColType } from '@mui/x-data-grid-premium'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ViewNumber from '@react/components/elements/ViewNumber'
import { ReportDescriptor } from '@react/reporting/ReportDescriptors'
import { erc20WeiToCommaDecString, otAssert, toCommaDecString } from '@shared/conversions'

import { ArrayTypeEnum, ReportingColumnVisibilityEnum, StringTypeEnum } from '@shared/SchemaTypes'

import ViewLogo from '@react/components/elements/ViewLogo'
import { PoolAccessControlCTO, VaultAccessStatusEnum } from '@shared/CompanyProfileCTO'
import { toCompanyAddrString } from '@react/fetchers/companyProfileFetchers'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { ReactNode } from 'react'
import * as yup from 'yup'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { PoolEventCTO } from '@shared/PoolEventCTO'
import { FeeAccrualAccountCTO } from '@shared/FeeAccrualCTO'

dayjs.extend(utc)
dayjs.extend(timezone)

function ExplorerHeader(props: { label: string; description?: string }): React.ReactElement {
  const { label } = props
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }} title={props.description}>
      <Typography
        sx={{
          wordBreak: 'break-word',
          display: 'inline-block',
          maxWidth: '100%',
        }}
        bgcolor={theme.palette.background.default}
        color={theme.palette.info.main}
        align={'left'}
        variant={'captionBold'}
      >
        {label}
      </Typography>
    </Box>
  )
}

const valueFromRow = (row: any, property: string): any => {
  const propArray = property.split('.')
  let value = row
  for (let i = 0; i < propArray.length; i++) {
    const prop = propArray[i]
    otAssert(prop, `prop is undefined ${property} `)
    otAssert(value, `prop is undefined ${property} `)

    value = value[prop]

    if (value === undefined) {
      return undefined
    }
  }
  return value
}
export const columnFromSchema = (
  fieldSchemaDescription: yup.SchemaDescription,
  property: string
): GridColDef | undefined => {
  const label: string = fieldSchemaDescription.label ?? ' '
  const stringType = fieldSchemaDescription.meta!.stringType
  const arrayType = fieldSchemaDescription.meta?.arrayType
  const description = fieldSchemaDescription.meta?.description ?? label
  if (fieldSchemaDescription.meta?.columnVisibility == ReportingColumnVisibilityEnum.Disabled) {
    return undefined
  }
  const fieldType = fieldSchemaDescription.type
  let columnType = fieldType
  if (
    fieldType === 'string' &&
    (stringType === StringTypeEnum.STABLE_COINS || stringType === StringTypeEnum.PPT_SHARES)
  ) {
    columnType = 'erc20'
  }
  if (stringType === StringTypeEnum.ETH_DATE) {
    columnType = 'string'
  }
  if (stringType === StringTypeEnum.ETH_DATE_TIME) {
    columnType = 'string'
  }
  let sortComparator: undefined | ((a: any, b: any) => number) = undefined
  if (stringType === StringTypeEnum.ETH_DATE_TIME_STRING) {
    columnType = 'string'
    sortComparator = (a, b) => {
      const aSplit = a.split(/[-\s]/)
      const bSplit = b.split(/[-\s]/)
      const aFlip: string = aSplit[2] + aSplit[1] + aSplit[0]
      const bFlip: string = bSplit[2] + bSplit[1] + bSplit[0]

      return Number(aFlip) - Number(bFlip)
    }
  }
  return {
    field: property,
    renderHeader: (_): ReactNode => {
      return <ExplorerHeader label={label} description={description} />
    },
    headerName: label,
    align: 'left',
    headerAlign: 'left',
    type: columnType as GridColType,
    width: fieldSchemaDescription.meta?.columnWidth ?? 150,
    sortComparator: sortComparator,
    renderCell: (params) => {
      let value = valueFromRow(params.row, property)
      if (params.aggregation) {
        value = params.value
      }

      const poolTokenSymbol =
        params.row.symbol ?? params.row.poolOverviewCTO?.symbol ?? params.row.poolAccountCTO?.symbol ?? ''
      const liquidityTokenSymbol =
        params.row.liquidityTokenSymbol ??
        params.row.poolOverviewCTO?.liquidityTokenSymbol ??
        params.row.poolAccountCTO?.liquidityTokenSymbol ??
        ''

      if (stringType === StringTypeEnum.ETH_ADDR) {
        if (!value) return <Box sx={{ textAlign: 'center', marginRight: 6 }}>-</Box>
        return (
          <AddressPill
            addr={value}
            style={{ height: 20 }}
            labelStyle={{ fontSize: '12px', lineHeight: '1', zIndex: 5 }}
          />
        )
      }

      if (stringType === StringTypeEnum.TX_HASH) {
        return (
          <div className="">
            <AddressPill
              addr={value}
              style={{ height: 20 }}
              labelStyle={{ fontSize: '12px', lineHeight: '12px', zIndex: 5 }}
              hasEtherScanLink={true}
            />
          </div>
        )
      }
      const stateToString: undefined | Record<string, string> = fieldSchemaDescription.meta?.stateToString
      if (value && stateToString) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            <Typography align={'left'} variant={'caption'}>
              {stateToString[value]}
            </Typography>
          </Box>
        )
      }

      if (
        fieldType === 'number' ||
        stringType === StringTypeEnum.EXCHANGE_RATE ||
        stringType === StringTypeEnum.EXCHANGE_RATE_LONG ||
        stringType === StringTypeEnum.STABLE_COINS ||
        stringType === StringTypeEnum.PPT_SHARES ||
        stringType === StringTypeEnum.APR ||
        stringType === StringTypeEnum.SINGLE_DAY_APR
      ) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {value !== undefined && (
              <ViewNumber
                value={value}
                label={label}
                inTable={true}
                fieldSchema={fieldSchemaDescription}
                dataValue={value}
                poolTokenSymbol={poolTokenSymbol}
                liquidityTokenSymbol={liquidityTokenSymbol}
              />
            )}
          </Box>
        )
      }

      if (fieldType === 'array') {
        const arrayValue = (value as any[]) ?? []
        if (arrayType === ArrayTypeEnum.VAULT_EVENT) {
          return (
            <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
              {arrayValue.map((poolEventCTO: PoolEventCTO, index: number) => {
                return (
                  <Box key={'pool-event-' + index} sx={{ display: 'flex', flexGrow: 1, flexDirection: 'row' }}>
                    <Typography key={'status-' + index} align={'left'} marginRight={1} variant={'caption'}>
                      {poolEventCTO.eventName}
                    </Typography>
                    {poolEventCTO.assets !== '0' && (
                      <Typography key={'display-name-' + index} align={'left'} variant={'caption'}>
                        {`${erc20WeiToCommaDecString(poolEventCTO.assets)} ${poolEventCTO.liquidityTokenSymbol}`}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>
          )
        } else if (arrayType === ArrayTypeEnum.FEE_ACCRUAL_ACCOUNT) {
          return (
            <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
              {arrayValue.map((feeAccount: FeeAccrualAccountCTO, index: number) => {
                return (
                  <Box key={'pool-event-' + index} sx={{ display: 'flex', flexGrow: 1, flexDirection: 'row' }}>
                    <Typography key={'status-' + index} align={'left'} marginRight={1} variant={'caption'}>
                      {(feeAccount.accountAddr?.slice(0, 8) ?? '') + '...'}
                    </Typography>

                    <Typography
                      key={'fee-accrual-' + index}
                      align={'left'}
                      variant={'caption'}
                      style={{ marginRight: 6 }}
                    >
                      {`${erc20WeiToCommaDecString(feeAccount.feeAccrual)}`}
                    </Typography>
                    <Typography key={'lifetime-accrued-fees-' + index} align={'left'} variant={'caption'}>
                      {`${erc20WeiToCommaDecString(feeAccount.lifetimeAccruedFees)} `}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          )
        } else if (arrayType === ArrayTypeEnum.POOL_ACCESS_CONTROL) {
          return (
            <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
              {arrayValue
                .filter(
                  (poolAccessControlCTO: PoolAccessControlCTO) =>
                    poolAccessControlCTO.status === VaultAccessStatusEnum.REQUESTED ||
                    poolAccessControlCTO.status === VaultAccessStatusEnum.ALLOWED
                )
                .map((poolAccessControlCTO: PoolAccessControlCTO, index: number) => {
                  const displayName = poolAccessControlCTO?.displayName || poolAccessControlCTO.poolName
                  return (
                    <Box
                      key={'pool-access-control-' + index}
                      sx={{ display: 'flex', flexGrow: 1, flexDirection: 'row' }}
                    >
                      {poolAccessControlCTO.status === VaultAccessStatusEnum.REQUESTED && (
                        <Typography key={'status-' + index} align={'left'} marginRight={1} variant={'caption'}>
                          {poolAccessControlCTO.status}
                        </Typography>
                      )}

                      <Typography key={'display-name-' + index} align={'left'} variant={'caption'}>
                        {displayName}
                      </Typography>
                      <Typography key={'addr-' + index} marginLeft={1} align={'left'} variant={'caption'}>
                        {(poolAccessControlCTO.walletAddr?.slice(0, 8) ?? '') + '...'}
                      </Typography>
                    </Box>
                  )
                })}
            </Box>
          )
        } else {
          return (
            <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
              {arrayValue.map((item: object, index: number) => {
                if (index < 2) {
                  return (
                    <Typography key={index} align={'left'} variant={'caption'}>
                      {JSON.stringify(item)}
                    </Typography>
                  )
                }
                return <></>
              })}
              {arrayValue.length >= 2 && (
                <Typography align={'left'} variant={'caption'}>
                  ...
                </Typography>
              )}
            </Box>
          )
        }
      }

      if (fieldType === 'mixed' && typeof value === 'bigint') {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>{toCommaDecString(value.toString())}</Box>
        )
      }
      return (
        <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
          <Typography align={'left'} variant={'caption'}>
            {value?.toString()}
          </Typography>
        </Box>
      )
    },

    valueFormatter: (value: any) => {
      if (value === undefined) {
        return ''
      }

      if (stringType === StringTypeEnum.STABLE_COINS) {
        return `${(value / 1e6).toFixed(2)}`
      }
      if (stringType === StringTypeEnum.PPT_SHARES) {
        return `${(value / 1e6).toFixed(2)}`
      }

      if (stringType === StringTypeEnum.ETH_DATE) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('YYYY-MM-DD UTC')
      }
      if (stringType === StringTypeEnum.ETH_TIME) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('HH:mm:ss UTC')
      }
      if (stringType === StringTypeEnum.ETH_DATE_TIME) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('YYYY-MM-DD HH:mm:ss UTC')
      }
      if (stringType === StringTypeEnum.ETH_TIME_OF_DAY) {
        // seconds from 0:00 UTC e.g. 64800 is 18:00 UTC
        const secondsSinceMidnight = Number(value)
        const hour = Math.floor(secondsSinceMidnight / 3600)
        const rem = secondsSinceMidnight % 3600
        const min = Math.floor(rem / 60)
        const minDisplay = min < 10 ? `0${min}` : `${min}`
        return `${hour}:${minDisplay}  UTC`
      }
      if (stringType === StringTypeEnum.SINGLE_DAY_APR) {
        return `${(Number(value) / 1e8 / 1e8).toFixed(6)} %`
      }
      if (stringType === StringTypeEnum.APR) {
        return `${(Number(value) / 1e2).toFixed(2)} %`
      }
      if (fieldType === 'array') {
        if (arrayType === ArrayTypeEnum.VAULT_EVENT) {
          const strArray: string[] = []

          value.forEach((poolEventCTO: PoolEventCTO) => {
            strArray.push(
              `${poolEventCTO.eventName} ${erc20WeiToCommaDecString(poolEventCTO.assets)} ${poolEventCTO.liquidityTokenSymbol}\n`
            )
          })

          const str = strArray.join('\n')
          return str
        }
        return JSON.stringify(value)
      }

      return `${value}`
    },

    valueGetter: (_value: any, row?: any, _data?: any, _apiRef?: any) => {
      // istanbul ignore next
      if (!row) return ''
      const value = valueFromRow(row, property)
      if (
        stringType === StringTypeEnum.ETH_DATE ||
        stringType === StringTypeEnum.ETH_DATE_TIME ||
        stringType === StringTypeEnum.ETH_TIME
      ) {
        if (typeof value === 'string') {
          const aSplit = value.split(/[-\s]/)
          const d = new Date(Date.UTC(Number(aSplit[2]), Number(aSplit[1]) - 1, Number(aSplit[0]), 0, 0, 0))

          return d
        }
        const d = new Date(Number(value) * 1000)

        return d
        //        return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()} UTC`
        //        return (value + 2209161600) / (3600 * 24)
      }

      return value
    },
  }
}

type ColumnInfo = {
  prop: string
  columnOrder: number
  columnDef: GridColDef
}

function buildColumnsRecursive(
  prefix: string,
  fields: Record<string, yup.SchemaDescription>,
  reportDescriptor: ReportDescriptor,
  columnInfos: ColumnInfo[],
  columnVisibilityModel: Record<string, boolean>
) {
  const keys = Object.keys(fields)

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const field = fields[key]
    const prop = prefix + key
    if (field.type === 'object') {
      buildColumnsRecursive(
        prop + '.',
        (field as yup.SchemaObjectDescription).fields as Record<string, yup.SchemaDescription>,
        reportDescriptor,
        columnInfos,
        columnVisibilityModel
      )
    } else {
      const column = columnFromSchema(field, prop)
      // istanbul ignore next
      if (column) {
        const columnVisibility = field.meta?.columnVisibility ?? ReportingColumnVisibilityEnum.DefaultHidden
        columnInfos.push({
          prop,
          columnOrder: field.meta?.columnOrder ?? Number.MAX_SAFE_INTEGER,
          columnDef: column,
        })
        columnVisibilityModel[prop] = columnVisibility === ReportingColumnVisibilityEnum.DefaultVisible
      }
    }
  }
}

export function reportColumnsForDescriptor(
  reportDescriptor: ReportDescriptor,
  companyProfiles?: CompanyProfileCTO[] // Make this optional
): {
  columns: GridColDef[]
  columnVisibilityModel: Record<string, boolean>
  columnOrder: Record<string, number>
} {
  const columnInfos: ColumnInfo[] = []
  const columnVisibilityModel: Record<string, boolean> = {}
  buildColumnsRecursive(
    '',
    reportDescriptor.schema.describe().fields as Record<string, yup.SchemaDescription>,
    reportDescriptor,
    columnInfos,
    columnVisibilityModel
  )

  const columns: GridColDef[] = columnInfos.sort((a, b) => a.columnOrder - b.columnOrder).map((ci) => ci.columnDef)
  // prettier-ignore

  if (reportDescriptor.logoProp) {
    const col: GridColDef = {
      field: 'logo',
      width: 30,
      type: 'string',
      headerName: 'Logo',
      renderCell: (params) => {
        if (reportDescriptor.logoProp) {
          return (<ViewLogo size={30} addrOrId={params.row[reportDescriptor.logoProp]} />)
        }
        else { return (<></>) }
      },
    }
    columns.unshift(col)
    columnInfos.push({
      prop: 'logo',
      columnOrder: 1,
      columnDef: col,
    })
  }
  if (reportDescriptor.actionColumns) {
    const col: GridColDef = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 160,
      getActions: reportDescriptor.actionColumns,
    }
    columns.push(col)
    columnInfos.push({
      prop: 'actions',
      columnOrder: columns.length,
      columnDef: col,
    })
  }
  if (reportDescriptor.companyNameProp && companyProfiles) {
    const col: GridColDef = {
      field: 'companyName',
      headerName: 'Company Name',
      width: 150,
      renderHeader: (_): ReactNode => {
        return <ExplorerHeader label="Company Name" />
      },
      renderCell: (params) => {
        const { companyProfile } = toCompanyAddrString(params.row[reportDescriptor.companyNameProp!], companyProfiles)
        const companyName = companyProfile ? companyProfile.companyName : '-'
        return <Typography variant="caption">{companyName}</Typography>
      },
    }
    columns.push(col)
    columnInfos.push({
      prop: 'companyName',
      columnOrder: columns.length,
      columnDef: col,
    })
  }
  const columnOrder: Record<string, number> = {}
  columnInfos
    .sort((a, b) => a.columnOrder - b.columnOrder)
    .forEach((col, index) => {
      columnOrder[col.prop] = index
    })

  return { columns, columnVisibilityModel, columnOrder }
}

export const ReportingColumnsTestExports = {
  columnFromSchema,
}
